import styled from 'styled-components';

export const PreviewContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	align-items: center;
	justify-content: center;
	min-height: 60px;
`;

export const ValueText = styled.div`
	font-size: 12px;
	color: ${({ theme }) => theme.gray[600]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	text-align: center;
`;

export const ColorSwatch = styled.div<{ color: string }>`
	width: 100%;
	height: 60px;
	background-color: ${({ color }) => color};
	border-radius: 4px;
	border: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const LengthBar = styled.div<{ width: number }>`
	width: ${({ width }) => Math.min(width, 180)}px;
	height: 8px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
`;

export const ShadowBox = styled.div<{ shadow: string }>`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[0]};
	border-radius: 4px;
	box-shadow: ${({ shadow }) => shadow};
`;

export const FontSample = styled.div<{ fontFamily: string }>`
	font-size: 24px;
	font-weight: 600;
	font-family: ${({ fontFamily }) => fontFamily};
	color: ${({ theme }) => theme.gray[1000]};
`;

export const SizeSample = styled.div<{ fontSize: number }>`
	font-size: ${({ fontSize }) => fontSize}px;
	color: ${({ theme }) => theme.gray[1000]};
	line-height: 1.2;
`;

export const LineHeightSample = styled.div<{ lineHeight: number }>`
	font-size: 14px;
	line-height: ${({ lineHeight }) => lineHeight};
	color: ${({ theme }) => theme.gray[1000]};
	width: 100%;
`;

export const WeightSample = styled.div<{ fontWeight: number }>`
	font-size: 16px;
	font-weight: ${({ fontWeight }) => fontWeight};
	color: ${({ theme }) => theme.gray[1000]};
`;

export const RadiusBox = styled.div<{ borderRadius: number }>`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: ${({ borderRadius }) => borderRadius}px;
`;

export const OpacityBox = styled.div<{ opacity: number }>`
	width: 60px;
	height: 60px;
	background: linear-gradient(45deg, ${({ theme }) => theme.gray[200]} 25%, transparent 25%),
		linear-gradient(-45deg, ${({ theme }) => theme.gray[200]} 25%, transparent 25%),
		linear-gradient(45deg, transparent 75%, ${({ theme }) => theme.gray[200]} 75%),
		linear-gradient(-45deg, transparent 75%, ${({ theme }) => theme.gray[200]} 75%);
	background-size: 8px 8px;
	background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
	border-radius: 4px;
	position: relative;
`;

export const OpacityOverlay = styled.div<{ opacity: number }>`
	width: 100%;
	height: 100%;
	background-color: ${({ theme }) => theme.gray[1000]};
	opacity: ${({ opacity }) => opacity};
	border-radius: 4px;
`;

export const TransitionBox = styled.div<{ duration: string; timingFunction: string }>`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
	transition: transform ${({ duration }) => duration} ${({ timingFunction }) => timingFunction};

	&:hover {
		transform: scale(1.1);
	}
`;

export const SimpleValue = styled.div`
	font-size: 14px;
	color: ${({ theme }) => theme.gray[800]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	text-align: center;
	padding: 8px;
`;

export const TokenCard = styled.div<{ selected?: boolean }>`
	background-color: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme, selected }) => (selected ? theme.blue[500] : theme.gray[200])};
	border-radius: 8px;
	padding: 16px;
	transition: all 0.2s ease;
	min-height: 140px;
	display: flex;
	flex-direction: column;
	gap: 12px;

	&:hover {
		border-color: ${({ theme, selected }) => (selected ? theme.blue[500] : theme.gray[400])};
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}
`;

export const TokenName = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[1000]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
`;

// Horizontal gap visualization (two boxes with gap)
export const SpacingVisualization = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
`;

export const SpacingBox = styled.div`
	width: 24px;
	height: 24px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
	flex-shrink: 0;
`;

export const SpacingGap = styled.div<{ spacing: number }>`
	width: ${({ spacing }) => Math.min(Math.max(spacing, 4), 120)}px;
	height: 2px;
	background-color: ${({ theme }) => theme.gray[200]};
	margin: 0 4px;
	position: relative;

	&::before,
	&::after {
		content: '';
		position: absolute;
		top: 50%;
		width: 0;
		height: 0;
		border-left: 4px solid ${({ theme }) => theme.gray[400]};
		border-top: 3px solid transparent;
		border-bottom: 3px solid transparent;
	}

	&::before {
		left: -4px;
		transform: translateY(-50%);
	}

	&::after {
		right: -4px;
		transform: translateY(-50%) rotate(180deg);
	}
`;

// Vertical stack visualization
export const SpacingVisualizationVertical = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
`;

export const SpacingGapVertical = styled.div<{ spacing: number }>`
	width: 2px;
	height: ${({ spacing }) => Math.min(Math.max(spacing, 4), 80)}px;
	background-color: ${({ theme }) => theme.gray[200]};
	margin: 4px 0;
	position: relative;

	&::before,
	&::after {
		content: '';
		position: absolute;
		left: 50%;
		width: 0;
		height: 0;
		border-top: 4px solid ${({ theme }) => theme.gray[400]};
		border-left: 3px solid transparent;
		border-right: 3px solid transparent;
	}

	&::before {
		top: -4px;
		transform: translateX(-50%);
	}

	&::after {
		bottom: -4px;
		transform: translateX(-50%) rotate(180deg);
	}
`;

// Box with padding visualization
export const BoxPaddingVisualization = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
`;

export const BoxPaddingOuter = styled.div<{ spacing: number }>`
	padding: ${({ spacing }) => Math.min(Math.max(spacing, 4), 40)}px;
	background-color: ${({ theme }) => theme.gray[100]};
	border: 1px dashed ${({ theme }) => theme.gray[200]};
	border-radius: 4px;
`;

export const BoxPaddingInner = styled.div`
	width: 32px;
	height: 32px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
`;

// Spacing visualization: Fixed box (60px) with boxes on left/right showing spacing
export const BoxPaddingHorizontal3 = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	gap: 0;
`;

export const FixedBox = styled.div`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
	flex-shrink: 0;
`;

export const SpacingBoxLeft = styled.div<{ spacing: number }>`
	width: ${({ spacing }) => Math.min(Math.max(spacing, 4), 80)}px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[200]};
	border-radius: 4px;
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
	flex-shrink: 0;
`;

export const SpacingBoxRight = styled.div<{ spacing: number }>`
	width: ${({ spacing }) => Math.min(Math.max(spacing, 4), 80)}px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[200]};
	border-radius: 4px;
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
	flex-shrink: 0;
`;

export const TokenGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
	gap: 24px;
	width: 100%;
`;
